import axios, { AxiosRequestConfig } from 'axios';

import { AxiosServiceResponse } from 'interfaces/general';

const instance = axios.create({
  baseURL: `${process.env.TICKETS_SERVICE_ENDPOINT}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  }
});

export type TicketApiEnvelope<T> = {
  data: T;
  message: string;
  isError: boolean;
};

export type ItilTranslationType =
  | 'Description'
  | 'CreationComment'
  | 'CloseComment';

export type ItilTranslation = {
  translationType: ItilTranslationType;
  translatedText: string;
};

/** @deprecated Use ItilTranslation */
export type ItilTranslationData = {
  translatedText: string;
  translationType?: ItilTranslationType;
};

export type ItilTranslateRequest = {
  rawDescription: string;
  templateKey?: string;
  translationType?: ItilTranslationType;
};

/** Normaliza saltos de línea para un payload JSON estable. */
export function normalizeItilRawDescription(value: string): string {
  return value.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
}

/** Serializa el body con JSON.stringify para escapar \\n y caracteres de control. */
export function serializeItilTranslateRequest(
  data: ItilTranslateRequest
): string {
  const payload: ItilTranslateRequest = {
    rawDescription: normalizeItilRawDescription(data.rawDescription ?? ''),
    ...(data.templateKey ? { templateKey: data.templateKey } : {}),
    ...(data.translationType ? { translationType: data.translationType } : {})
  };
  return JSON.stringify(payload);
}

const MOCK_ENABLED = process.env.REACT_APP_ITIL_GENIUS_MOCK === 'true';

const mockStorageKey = (
  ticketId: number,
  translationType: ItilTranslationType
) => `itil-mock-${ticketId}-${translationType}`;

const mockTranslate = (rawDescription: string): Promise<string> =>
  new Promise((resolve, reject) => {
    setTimeout(() => {
      if (!rawDescription?.trim()) {
        reject(new Error('La descripción del ticket es obligatoria.'));
        return;
      }
      if (process.env.REACT_APP_ITIL_GENIUS_MOCK_DOWN === 'true') {
        reject(new Error('Tiempo de espera agotado al transformar con ITIL Genius.'));
        return;
      }
      resolve(
        rawDescription
          .split(/[.!?]\s+/)
          .filter(Boolean)
          .map((s) => s.trim().charAt(0).toUpperCase() + s.trim().slice(1))
          .join('. ')
      );
    }, 1200);
  });

function unwrapTranslation(
  response: AxiosServiceResponse<TicketApiEnvelope<ItilTranslation | ''>>
): string {
  const body = response.data;
  if (body.isError) {
    throw new Error(body.message || 'Error al traducir con ITIL Genius.');
  }
  const text = (body.data as ItilTranslation)?.translatedText;
  if (!text) {
    throw new Error('Respuesta ITIL sin texto traducido.');
  }
  return text;
}

function unwrapList(
  response: AxiosServiceResponse<TicketApiEnvelope<ItilTranslation[]>>
): ItilTranslation[] {
  const body = response.data;
  if (body.isError) {
    throw new Error(body.message || 'Error al obtener traducciones ITIL.');
  }
  return body.data ?? [];
}

/**
 * Devuelve el texto del snapshot más reciente.
 * El API lista historial cronológico; el último elemento es la versión vigente.
 */
export function getLatestItilTranslation(
  items: ItilTranslation[]
): string | null {
  if (!items.length) {
    return null;
  }
  const last = items[items.length - 1];
  return last?.translatedText?.trim() || null;
}

async function listTranslations(
  ticketId: number,
  translationType?: ItilTranslationType,
  config: AxiosRequestConfig = {}
): Promise<ItilTranslation[]> {
  if (MOCK_ENABLED) {
    const types: ItilTranslationType[] = translationType
      ? [translationType]
      : ['Description', 'CreationComment', 'CloseComment'];
    return types
      .map((type) => {
        const text = sessionStorage.getItem(mockStorageKey(ticketId, type));
        return text ? { translationType: type, translatedText: text } : null;
      })
      .filter((item): item is ItilTranslation => item !== null);
  }
  try {
    const query = translationType
      ? `?translationType=${encodeURIComponent(translationType)}`
      : '';
    const response = await instance.request<
      void,
      AxiosServiceResponse<TicketApiEnvelope<ItilTranslation[]>>
    >({
      url: `/Tickets/${ticketId}/itil/translations${query}`,
      method: 'GET',
      ...config
    });
    return unwrapList(response);
  } catch {
    return [];
  }
}

export default {
  instance,

  /**
   * Preview sin ticket (creación de ticket).
   * POST /Tickets/itil/translate
   */
  TranslatePreview: async (
    data: ItilTranslateRequest,
    config: AxiosRequestConfig = {}
  ): Promise<string> => {
    if (MOCK_ENABLED) {
      return mockTranslate(data.rawDescription);
    }
    const response = await instance.request<
      string,
      AxiosServiceResponse<TicketApiEnvelope<ItilTranslation>>
    >({
      url: '/Tickets/itil/translate',
      method: 'POST',
      data: serializeItilTranslateRequest(data),
      ...config
    });
    return unwrapTranslation(response);
  },

  /**
   * Traducir y persistir para un ticket.
   * POST /Tickets/{ticketId}/itil/translate
   */
  TranslateTicket: async (
    ticketId: number,
    data: ItilTranslateRequest,
    config: AxiosRequestConfig = {}
  ): Promise<string> => {
    if (MOCK_ENABLED) {
      const text = await mockTranslate(data.rawDescription);
      const type = data.translationType ?? 'Description';
      sessionStorage.setItem(mockStorageKey(ticketId, type), text);
      return text;
    }
    const response = await instance.request<
      string,
      AxiosServiceResponse<TicketApiEnvelope<ItilTranslation>>
    >({
      url: `/Tickets/${ticketId}/itil/translate`,
      method: 'POST',
      data: serializeItilTranslateRequest(data),
      ...config
    });
    return unwrapTranslation(response);
  },

  /**
   * Listar snapshots persistidos.
   * GET /Tickets/{ticketId}/itil/translations
   */
  ListTranslations: listTranslations,

  /**
   * Obtener traducción persistida por tipo. Retorna null si no hay snapshot.
   */
  GetTranslationByType: async (
    ticketId: number,
    translationType: ItilTranslationType,
    config: AxiosRequestConfig = {}
  ): Promise<string | null> => {
    const items = await listTranslations(ticketId, translationType, config);
    return getLatestItilTranslation(items);
  },

  /** Persist mock translation for dev (e.g. SaveTicket flow) */
  MockPersistTranslation: (
    ticketId: number,
    text: string,
    translationType: ItilTranslationType = 'Description'
  ) => {
    if (MOCK_ENABLED) {
      sessionStorage.setItem(mockStorageKey(ticketId, translationType), text);
    }
  }
};
